import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { FieldArray, Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import { accreditingBodySchema } from "../../validators/accreditingBodyValidators";
import DynamicTextInput from "../../components/Inputs/DynamicTextInput";
import {
  createSession,
  deleteSession,
  getSessions,
  updateSession,
} from "../../services/sessionService";

const AccreditingBody = () => {
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const filtered = sessions.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSessions(filtered);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, sessions]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const { data } = await getSessions();
      setSessions(data.data);
      setFilteredSessions(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load Sessions");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (sessionToEdit) {
      onUpdateSession(payload, resetForm);
    } else {
      const nonEmptySessions = payload?.sessions.filter(
        (session) => session?.name !== ""
      );

      if (nonEmptySessions.length <= 0) {
        toast.error("Sessions cannot be empty!");

        return;
      }
      onCreateSession(nonEmptySessions, resetForm);
    }
  };

  const onCreateSession = async (sessionsArray, resetForm) => {
    setErrs(null);

    try {
      const promises = sessionsArray.map(async (session) => {
        const payload = { name: session.name };

        const { data } = await createSession(payload);

        return data;
      });

      // Wait for all the createDepartment requests to complete
      const createdSessions = await Promise.all(promises);

      setSessions((prev) => [...prev, ...createdSessions]);

      toast.success("Session(s) created successfully");

      setIsModalOpen(false);

      resetForm({ values: { sessions: [{ name: "" }] } });

      await loadSessions();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Some Sessions were not created, try again!"
        );
      }
    }
  };

  const handleEdit = (accreditingBody) => {
    setSessionToEdit(accreditingBody);
    setIsModalOpen(true);
  };

  const onUpdateSession = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalSessions = [...sessions];

      const sessionIndex = originalSessions.findIndex(
        (session) => session.id === sessionToEdit.id
      );

      if (sessionIndex === -1) throw new Error("Session not found.");

      const { data } = await updateSession(sessionToEdit.id, payload);

      originalSessions[sessionIndex] = data.data;

      setSessions(originalSessions);

      setSessionToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Session updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response?.data?.error || "Session not updated, try again!");
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setSessionToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteSession = async () => {
    const originalSessions = [...sessions];

    const newSession = originalSessions.filter(
      (accreditingBody) => accreditingBody.id !== sessionToDelete.id
    );

    setSessions(newSession);

    try {
      await deleteSession(sessionToDelete?.id);

      toast.success("Session deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Session not deleted, try again later"
        }`
      );

      setSessions(originalSessions);
    } finally {
      setIsDeleteModalOpen(false);

      setSessionToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setSessionToEdit(null);
    setSessionToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 300,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },

    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Year">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Year">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Years" subtitle1="Years" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Year"
            count={filteredSessions.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Year"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredSessions.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {sessionToEdit ? "Edit Year" : "Add Year"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            {sessionToEdit ? (
              <Formik
                initialValues={{
                  name: sessionToEdit ? sessionToEdit?.name : "",
                }}
                enableReinitialize
                validationSchema={accreditingBodySchema}
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => {
                  return (
                    <Form className="space-y-5 px-4! py-4!">
                      {errs && <FormErrors errors={errs} />}

                      <TextInput
                        name="name"
                        title="Name"
                        placeholder="Enter Session"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        autoFocus
                      />

                      <div className="flex justify-end gap-2">
                        <FormCancelButton
                          text="Cancel"
                          onClick={handleCancel}
                        />
                        <FormButton
                          text="Save"
                          type="primary"
                          onClick={submitForm}
                        />
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            ) : (
              <Formik
                initialValues={{
                  sessions: [{ name: "" }],
                }}
                enableReinitialize
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => (
                  <Form className="space-y-2 !px-5 pb-3">
                    {errs && <FormErrors errors={errs} />}
                    <FieldArray name="sessions">
                      {({ remove, push }) =>
                        (values.sessions || []).map(
                          (accreditingBody, index) => (
                            <DynamicTextInput
                              key={index}
                              index={index}
                              name={`sessions.${index}.name`}
                              placeholder="Enter Session"
                              requiredField={true}
                              errors={errors}
                              values={values}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              push={push}
                              remove={remove}
                              pushName="sessions"
                              dynamicFieldMap={{
                                auditCategories: {
                                  name: "",
                                },
                              }}
                            />
                          )
                        )
                      }
                    </FieldArray>
                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteSession}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default AccreditingBody;
